// Copyright (c) John Nesky and contributing authors, distributed under the MIT license, see accompanying the LICENSE.md file.

import {Config} from "../synth/SynthConfig.js";
import {isMobile} from "./EditorConfig.js";
import {ColorConfig} from "./ColorConfig.js";
import {Layout} from "./Layout.js";
import {Pattern, Channel, Song, Synth} from "../synth/synth.js";
import {SongRecovery, generateUid, errorAlert} from "./SongRecovery.js";
import {SongPerformance} from "./SongPerformance.js";
import {MidiInputHandler} from "./MidiInput.js";
import {Selection} from "./Selection.js";
import {Preferences} from "./Preferences.js";
import {loadTakes, missingTakes} from "./Recordings.js";
import {Change} from "./Change.js";
import {ChangeNotifier} from "./ChangeNotifier.js";
import {ChangeSong, setDefaultInstruments, discardInvalidPatternInstruments} from "./changes.js";

import {fittedBaseOctave} from "./ChannelViewport.js";

interface HistoryState {
	canUndo: boolean;
	sequenceNumber: number;
	bar: number;
	channel: number;
	instrument: number;
	recoveryUid: string;
	prompt: string | null;
	selection: {x0: number, x1: number, y0: number, y1: number, start: number, end: number};
}

export class SongDocument {
	public song: Song;
	public synth: Synth;
	public performance: SongPerformance;
	public midiInputHandler: MidiInputHandler;
	public readonly notifier: ChangeNotifier = new ChangeNotifier(() => this._validateDocState());
	public readonly selection: Selection = new Selection(this);
	public readonly prefs: Preferences = new Preferences();
	public channel: number = 0;
	public bar: number = 0;
	public readonly recentPatternInstruments: number[][] = [];
	public readonly viewedInstrument: number[] = [];
	
	public trackVisibleBars: number = 16;
	public trackVisibleChannels: number = 4;
	public barScrollPos: number = 0;
	public channelScrollPos: number = 0;
	public prompt: string | null = null;
	
	public addedEffect: boolean = false;
	public addedEnvelope: boolean = false;
	public currentPatternIsDirty: boolean = false;
	
	private static readonly _maximumUndoHistory: number = 100;
	private _recovery: SongRecovery = new SongRecovery();
	private _recoveryUid: string;
	private _recentChange: Change | null = null;
	private _sequenceNumber: number = 0;
	private _lastSequenceNumber: number = 0;
	private _stateShouldBePushed: boolean = false;
	private _recordedNewSong: boolean = false;
	private _waitingToUpdateState: boolean = false;
	
	constructor() {
		ColorConfig.setTheme(this.prefs.colorTheme);
		Layout.setLayout(this.prefs.layout);
		
		if (window.sessionStorage.getItem("currentUndoIndex") == null) {
			window.sessionStorage.setItem("currentUndoIndex", "0");
			window.sessionStorage.setItem("oldestUndoIndex", "0");
			window.sessionStorage.setItem("newestUndoIndex", "0");
		}
		
		let songString: string = window.location.hash;
		if (songString == "") {
			songString = this._getHash();
		}
		try {
			this.song = new Song(songString);
			if (songString == "" || songString == undefined) {
				setDefaultInstruments(this.song);
				this.song.scale = this.prefs.defaultScale;
			}
		} catch (error) {
			errorAlert(error);
		}
		songString = this.song.toBase64String();
		this.synth = new Synth(this.song);
		this.synth.volume = this._calcVolume();
		this.synth.anticipatePoorPerformance = isMobile;
		
		let state: HistoryState | null = this._getHistoryState();
		if (state == null) {
			// When the page is first loaded, indicate that undo is NOT possible.
			state = {canUndo: false, sequenceNumber: 0, bar: 0, channel: 0, instrument: 0, recoveryUid: generateUid(), prompt: null, selection: this.selection.toJSON()};
		}
		if (state.recoveryUid == undefined) state.recoveryUid = generateUid();
		this._replaceState(state, songString);
		window.addEventListener("hashchange", this._whenHistoryStateChanged);
		window.addEventListener("popstate", this._whenHistoryStateChanged);
		this.notifier.watch(this._loadRecordings);
		this._loadRecordings();
		
		this.bar = state.bar | 0;
		this.channel = state.channel | 0;
		for (let i: number = 0; i <= this.channel; i++) this.viewedInstrument[i] = 0;
		this.viewedInstrument[this.channel] = state.instrument | 0;
		this._recoveryUid = state.recoveryUid;
		//this.barScrollPos = Math.max(0, this.bar - (this.trackVisibleBars - 6));
		this.prompt = state.prompt;
		this.selection.fromJSON(state.selection);
		this.selection.scrollToSelectedPattern();
		
		// For all input events, intercept them in the capture phase, before other event handlers
		// make changes to the model, and enqueue a task to render the view after the changes are
		// done but before the browser renders. Listening in the capture phase allows this code to
		// be respond to events even if stopImmediatePropagation is called. mouseenter and
		// mouseleave are ignored because they are immediately followed by mousemove. Animation
		// frames and midi events also sometimes update the model, but are not automatically
		// detected here so they have to manually call "renderNow" instead.
		for (const eventName of ["input", "change", "click", "keyup", "keydown", "mousedown", "mousemove", "mouseup", "touchstart", "touchmove", "touchend", "touchcancel", "pointerdown", "pointermove", "pointerup", "pointercancel"]) {
			window.addEventListener(eventName, this.notifier.enqueueTaskToNotifyWatchers, {capture: true});
		}
		
		this._validateDocState();
		this.performance = new SongPerformance(this);
		this.midiInputHandler = new MidiInputHandler(this);
	}
	
	public toggleDisplayBrowserUrl() {
		const state: HistoryState | null = this._getHistoryState();
		if (state == null) throw new Error("History state is null.");
		this.prefs.displayBrowserUrl = !this.prefs.displayBrowserUrl;
		this._replaceState(state, this.song.toBase64String());
	}
	
	private _getHistoryState(): HistoryState | null {
		if (this.prefs.displayBrowserUrl) {
			return window.history.state;
		} else {
			const json: any = JSON.parse(window.sessionStorage.getItem(window.sessionStorage.getItem("currentUndoIndex")!)!);
			return json == null ? null : json.state;
		}
	}
	
	private _getHash(): string {
		if (this.prefs.displayBrowserUrl) {
			return window.location.hash;
		} else {
			const json: any = JSON.parse(window.sessionStorage.getItem(window.sessionStorage.getItem("currentUndoIndex")!)!);
			return json == null ? "" : json.hash;
		}
	}
	
	private _replaceState(state: HistoryState, hash: string): void {
		if (this.prefs.displayBrowserUrl) {
			window.history.replaceState(state, "", "#" + hash);
		} else {
			window.sessionStorage.setItem(window.sessionStorage.getItem("currentUndoIndex") || "0", JSON.stringify({state, hash}));
			window.history.replaceState(null, "", location.pathname);
		}
	}
	
	private _pushState(state: HistoryState, hash: string): void {
		if (this.prefs.displayBrowserUrl) {
			window.history.pushState(state, "", "#" + hash);
		} else {
			let currentIndex: number = Number(window.sessionStorage.getItem("currentUndoIndex"));
			let oldestIndex: number = Number(window.sessionStorage.getItem("oldestUndoIndex"));
			currentIndex = (currentIndex + 1) % SongDocument._maximumUndoHistory;
			window.sessionStorage.setItem("currentUndoIndex", String(currentIndex));
			window.sessionStorage.setItem("newestUndoIndex", String(currentIndex));
			if (currentIndex == oldestIndex) {
				oldestIndex = (oldestIndex + 1) % SongDocument._maximumUndoHistory;
				window.sessionStorage.setItem("oldestUndoIndex", String(oldestIndex));
			}
			window.sessionStorage.setItem(String(currentIndex), JSON.stringify({state, hash}));
			window.history.replaceState(null, "", location.pathname);
		}
		this._lastSequenceNumber = state.sequenceNumber;
	}
	
	public hasRedoHistory(): boolean {
		return this._lastSequenceNumber > this._sequenceNumber;
	}
	
	private _forward(): void {
		if (this.prefs.displayBrowserUrl) {
			window.history.forward();
		} else {
			let currentIndex: number = Number(window.sessionStorage.getItem("currentUndoIndex"));
			let newestIndex: number = Number(window.sessionStorage.getItem("newestUndoIndex"));
			if (currentIndex != newestIndex) {
				currentIndex = (currentIndex + 1) % SongDocument._maximumUndoHistory;
				window.sessionStorage.setItem("currentUndoIndex", String(currentIndex));
				setTimeout(this._whenHistoryStateChanged);
			}
		}
	}
	
	private _back(): void {
		if (this.prefs.displayBrowserUrl) {
			window.history.back();
		} else {
			let currentIndex: number = Number(window.sessionStorage.getItem("currentUndoIndex"));
			let oldestIndex: number = Number(window.sessionStorage.getItem("oldestUndoIndex"));
			if (currentIndex != oldestIndex) {
				currentIndex = (currentIndex + SongDocument._maximumUndoHistory - 1) % SongDocument._maximumUndoHistory;
				window.sessionStorage.setItem("currentUndoIndex", String(currentIndex));
				setTimeout(this._whenHistoryStateChanged);
			}
		}
	}
	
	private readonly _lookedUpTakes: Set<string> = new Set();
	/** Brings in recorded takes this song uses from this device's storage
	 * (each looked up once; takes recorded elsewhere stay silent). */
	private _loadRecordings = (): void => {
		const wanted: string[] = missingTakes(this.song).filter(id => !this._lookedUpTakes.has(id));
		if (wanted.length == 0) return;
		for (const id of wanted) this._lookedUpTakes.add(id);
		loadTakes(this.song).then(loaded => { if (loaded.length > 0) this.notifier.changed(); }, () => {});
	}

	private _whenHistoryStateChanged = (): void => {
		if (this.synth.recording) {
			// Changes to the song while it's recording to could mess up the recording so just abort the recording.
			this.performance.abortRecording();
		}
		
		if (window.history.state == null && window.location.hash != "") {
			// The user changed the hash directly.
			this._sequenceNumber++;
			this._resetSongRecoveryUid();
			const state: HistoryState = {canUndo: true, sequenceNumber: this._sequenceNumber, bar: this.bar, channel: this.channel, instrument: this.viewedInstrument[this.channel], recoveryUid: this._recoveryUid, prompt: null, selection: this.selection.toJSON()};
			try {
				new ChangeSong(this, window.location.hash);
			} catch (error) {
				errorAlert(error);
			}
			this.prompt = state.prompt;
			if (this.prefs.displayBrowserUrl) {
				this._replaceState(state, this.song.toBase64String());
			} else {
				this._pushState(state, this.song.toBase64String());
			}
			this.forgetLastChange();
			this.renderNow();
			return;
		}
		
		const state: HistoryState | null = this._getHistoryState();
		if (state == null) throw new Error("History state is null.");
		
		// Abort if we've already handled the current state. 
		if (state.sequenceNumber == this._sequenceNumber) return;
		
		this.bar = state.bar;
		this.channel = state.channel;
		this.viewedInstrument[this.channel] = state.instrument;
		this._sequenceNumber = state.sequenceNumber;
		this.prompt = state.prompt;
		
		try {
			new ChangeSong(this, this._getHash());
		} catch (error) {
			errorAlert(error);
		}
		
		this._recoveryUid = state.recoveryUid;
		this.selection.fromJSON(state.selection);
		
		//this.barScrollPos = Math.min(this.bar, Math.max(this.bar - (this.trackVisibleBars - 1), this.barScrollPos));
		
		this.forgetLastChange();
		this.renderNow();
	}
	
	public renderNow(): void {
		this.notifier.notifyWatchers();
	}
	
	// Make sure the doc state is self-consistent.
	private _validateDocState(): void {
		const channelCount: number = this.song.getChannelCount();
		for (let i: number = this.recentPatternInstruments.length; i < channelCount; i++) {
			this.recentPatternInstruments[i] = [0];
		}
		this.recentPatternInstruments.length = channelCount;
		for (let i: number = 0; i < channelCount; i++) {
			if (i == this.channel) {
				if (this.song.patternInstruments) {
					const pattern: Pattern | null = this.song.getPattern(this.channel, this.bar);
					if (pattern != null) {
						this.recentPatternInstruments[i] = pattern.instruments.concat();
					}
				} else {
					const channel: Channel = this.song.channels[this.channel];
					for (let j: number = 0; j < channel.instruments.length; j++) {
						this.recentPatternInstruments[i][j] = j;
					}
					this.recentPatternInstruments[i].length = channel.instruments.length;
				}
			}
			discardInvalidPatternInstruments(this.recentPatternInstruments[i], this.song, i);
		}
		
		for (let i: number = this.viewedInstrument.length; i < channelCount; i++) {
			this.viewedInstrument[i] = 0;
		}
		this.viewedInstrument.length = channelCount;
		for (let i: number = 0; i < channelCount; i++) {
			if (this.song.patternInstruments && !this.song.layeredInstruments && i == this.channel) {
				const pattern: Pattern | null = this.song.getPattern(this.channel, this.bar);
				if (pattern != null) {
					this.viewedInstrument[i] = pattern.instruments[0];
				}
			}
			this.viewedInstrument[i] = Math.min(this.viewedInstrument[i] | 0, this.song.channels[i].instruments.length - 1);
		}
		
		const highlightedPattern: Pattern | null = this.getCurrentPattern();
		if (highlightedPattern != null && this.song.patternInstruments) {
			this.recentPatternInstruments[this.channel] = highlightedPattern.instruments.concat();
		}
		
		// Normalize selection.
		// I'm allowing the doc.bar to drift outside the box selection while playing
		// because it may auto-follow the playhead outside the selection but it would
		// be annoying to lose your selection just because the song is playing.
		if ((!this.synth.playing && (this.bar < this.selection.boxSelectionBar || this.selection.boxSelectionBar + this.selection.boxSelectionWidth <= this.bar)) ||
			this.channel < this.selection.boxSelectionChannel ||
			this.selection.boxSelectionChannel + this.selection.boxSelectionHeight <= this.channel ||
			this.song.barCount < this.selection.boxSelectionBar + this.selection.boxSelectionWidth ||
			channelCount < this.selection.boxSelectionChannel + this.selection.boxSelectionHeight ||
			(this.selection.boxSelectionWidth == 1 && this.selection.boxSelectionHeight == 1))
		{
			this.selection.resetBoxSelection();
		}
		
		this.barScrollPos     = Math.max(0, Math.min(this.song.barCount          - this.trackVisibleBars,     this.barScrollPos));
		this.channelScrollPos = Math.max(0, Math.min(this.song.getChannelCount() - this.trackVisibleChannels, this.channelScrollPos));
	}
	
	private _updateHistoryState = (): void => {
		if (!this._waitingToUpdateState) return;
		this._waitingToUpdateState = false;
		let hash: string;
		try {
			// Ensure that the song is not corrupted before saving it.
			hash = this.song.toBase64String();
		} catch (error) {
			errorAlert(error);
			return;
		}
		if (this._stateShouldBePushed) this._sequenceNumber++;
		if (this._recordedNewSong) {
			this._resetSongRecoveryUid();
		} else {
			this._recovery.saveVersion(this._recoveryUid, hash);
		}
		let state: HistoryState = {canUndo: true, sequenceNumber: this._sequenceNumber, bar: this.bar, channel: this.channel, instrument: this.viewedInstrument[this.channel], recoveryUid: this._recoveryUid, prompt: this.prompt, selection: this.selection.toJSON()};
		if (this._stateShouldBePushed) {
			this._pushState(state, hash);
		} else {
			this._replaceState(state, hash);
		}
		this._stateShouldBePushed = false;
		this._recordedNewSong = false;
	}
	
	public record(change: Change, replace: boolean = false, newSong: boolean = false): void {
		if (change.isNoop()) {
			this._recentChange = null;
			if (replace) this._back();
		} else {
			change.commit();
			this._recentChange = change;
			this._stateShouldBePushed = this._stateShouldBePushed || !replace;
			this._recordedNewSong = this._recordedNewSong || newSong;
			if (!this._waitingToUpdateState) {
				// Defer updating the url/history until all sequenced changes have
				// committed and the interface has rendered the latest changes to
				// improve perceived responsiveness.
				window.requestAnimationFrame(this._updateHistoryState);
				this._waitingToUpdateState = true;
			}
		}
	}
	
	public flushHistory(): void {
		this._updateHistoryState();
	}
	
	private _resetSongRecoveryUid(): void {
		this._recoveryUid = generateUid();
	}
	
	public openPrompt(prompt: string): void {
		if (this._waitingToUpdateState) this._updateHistoryState();
		this.prompt = prompt;
		const hash: string = this.song.toBase64String();
		this._sequenceNumber++;
		const state = {canUndo: true, sequenceNumber: this._sequenceNumber, bar: this.bar, channel: this.channel, instrument: this.viewedInstrument[this.channel], recoveryUid: this._recoveryUid, prompt: this.prompt, selection: this.selection.toJSON()};
		this._pushState(state, hash);
	}
	
	public undo(): void {
		// A fast undo may arrive before the animation-frame history write.
		if (this._waitingToUpdateState) this._updateHistoryState();
		const state: HistoryState | null = this._getHistoryState();
		if (state == null || state.canUndo) this._back();
	}
	
	public redo(): void {
		this._forward();
	}
	
	public setProspectiveChange(change: Change | null): void {
		this._recentChange = change;
	}
	
	public forgetLastChange(): void {
		this._recentChange = null;
	}
	
	public lastChangeWas(change: Change | null): boolean {
		return change != null && change == this._recentChange;
	}
	
	public goBackToStart(): void {
		this.bar = 0;
		this.channel = 0;
		this.barScrollPos = 0;
		this.channelScrollPos = 0;
		this.synth.snapToStart();
		this.notifier.changed();
	}
	
	public setVolume(val: number): void {
		this.prefs.volume = val;
		this.prefs.save();
		this.synth.volume = this._calcVolume();
	}
	
	private _calcVolume(): number {
		return Math.min(1.0, Math.pow(this.prefs.volume / 50.0, 0.5)) * Math.pow(2.0, (this.prefs.volume - 75.0) / 25.0);
	}
	
	public getCurrentPattern(barOffset: number = 0): Pattern | null {
		return this.song.getPattern(this.channel, this.bar + barOffset);
	}
	
	public getCurrentInstrument(barOffset: number = 0): number {
		return this.viewedInstrument[this.channel];
	}
	
	public getMobileLayout(): boolean {
		return window.innerWidth <= 710;
	}
	
	public getBarWidth(): number {
		return (!this.getMobileLayout() && this.prefs.enableChannelMuting && !this.getFullScreen()) ? 30 : 32;
	}
	
	public getFullScreen(): boolean {
		return !this.getMobileLayout() && (this.prefs.layout != "small");
	}
	
	public getVisibleOctaveCount(): number {
		return this.getFullScreen() ? this.prefs.visibleOctaves : Preferences.defaultVisibleOctaves;
	}
	
	public getVisiblePitchCount(): number {
		 return this.getVisibleOctaveCount() * Config.pitchesPerOctave + 1;
	}
	
	private readonly _channelViewports = new WeakMap<Channel, {octave: number, count: number, base: number}>();
	private readonly _manualViewports = new WeakSet<Channel>();

	public useManualChannelViewport(): void {
		this._manualViewports.add(this.song.channels[this.channel]);
	}

	/** Moves a channel's visible pitch window onto the notes of the pattern at
	 * this bar when any of them (bends included) are off screen, e.g. notes
	 * pasted from a channel in another register. Without this they could not be
	 * seen or edited: the octave scroll bar is hidden by default. */
	public revealPattern(channel: number, bar: number): void {
		if (this.song.getChannelIsNoise(channel)) return;
		const pattern = this.song.getPattern(channel, bar);
		if (pattern == null || pattern.notes.length == 0) return;
		const pitches: number[] = [];
		for (const note of pattern.notes) for (const pitch of note.pitches) for (const pin of note.pins) pitches.push(pitch + pin.interval);
		const count: number = this.getVisibleOctaveCount();
		const current: number = this.getBaseVisibleOctave(channel);
		const visible = (base: number) => pitches.filter(p => p >= base * Config.pitchesPerOctave && p <= (base + count) * Config.pitchesPerOctave).length;
		if (visible(current) == pitches.length) return;
		// The window showing the most of these notes, nearest the current one.
		let best: number = current, bestCount: number = -1;
		for (let base: number = 0; base <= Config.pitchOctaves - count; base++) {
			const shown: number = visible(base);
			if (shown > bestCount || (shown == bestCount && Math.abs(base - current) < Math.abs(best - current))) {
				best = base;
				bestCount = shown;
			}
		}
		const track: Channel = this.song.channels[channel];
		this._manualViewports.delete(track);
		this._channelViewports.set(track, {octave: track.octave, count, base: best});
	}

	public getBaseVisibleOctave(channel: number): number {
		const visibleOctaveCount: number = this.getVisibleOctaveCount();
		const track = this.song.channels[channel];
        const fallback = Math.max(0, Math.min(Config.pitchOctaves - visibleOctaveCount, Math.ceil(track.octave - visibleOctaveCount * 0.5)));
        if (this.song.getChannelIsNoise(channel) || this._manualViewports.has(track)) return fallback;
        let view = this._channelViewports.get(track);
        if (view && view.octave !== track.octave) return fallback;
        if (!view || view.count !== visibleOctaveCount) {
            view = {octave: track.octave, count: visibleOctaveCount, base: fittedBaseOctave(track, visibleOctaveCount, fallback)};
            this._channelViewports.set(track, view);
        }
        return view.base;
	}
}
